import {
  IFPSStatus,
  IFlightPairingStatus,
  IFPSFlightStatus,
  IFPSPairingCrewMap,
  IFPSDesiredCrew,
  IFPSActualCrew,
} from "@vmosolutions/oc-interfaces";

export const flightStatusMessages: { [key: number]: IFPSStatus } = {
  1: { priority: 1, className: "no_pairing", status: "No Pairing", message: "No pairing found" }, // RED
  2: {
    priority: 2,
    className: "missing_crew",
    status: "Missing Crew",
    message: "Required positions missing on linked pairing(s)",
  }, // YELLOW
  3: { priority: 3, className: "extra_crew", status: "Extra Crew", message: "Extra crew on linked pairing(s)" }, // BLUE
  4: {
    priority: 4,
    className: "extra_pos",
    status: "Extra Position",
    message: "Extra crew position on linked pairing(s)",
  }, // WHITE
  5: {
    priority: 5,
    className: "missing_ac_info",
    status: "Missing AC Info",
    message: "Missing aircraft info or Required Crew",
  }, // ORANGE
  //6: {
  //  priority: 6,
  //  className: "missing_optional",
  //  status: "Missing Optional",
  //  message: "Optional Crew missing from Pairing",
  //},
  7: { priority: 7, className: "ok", status: "Ok", message: "Flight Valid" }, // GREEN
};

export interface IFPSAssignedCrew extends IFPSDesiredCrew {
  crew: string[];
  isRequired?: boolean;
}

// Create pairing map, Each pairing has a status key and assigned crew array.
// Note: will include first/last name once available)
export const returnPairingMap = (actualCrew: IFPSActualCrew[]): IFPSPairingCrewMap => {
  // Create crew map
  const crewMap: IFPSPairingCrewMap = actualCrew.reduce((acc: { [key: string]: any }, value) => {
    const { pairing } = value;
    if (acc[pairing]?.hasOwnProperty("actualCrew")) {
      if (value.pairing === pairing) {
        acc[pairing]["actualCrew"].push(value);
      }
    } else {
      acc[pairing] = { actualCrew: value ? [value] : [] };
    }
    return acc;
  }, {});
  return crewMap;
};

/**
 * Function that will check desired vs actual crew slots and return appropriate status
 * @param desiredCrew - Array of desired crew positions as well as the count required.
 * @param actualCrew - Array of assigned crew
 * @returns - one status is returned (if more than one is met, prioritized status is used)
 */
const checkCrewSlots = (desiredCrew: IFPSDesiredCrew[], actualCrew: IFPSActualCrew[]): IFPSStatus => {
  // Define this up here and not in the actualCrew map!
  if (!desiredCrew || !Array.isArray(desiredCrew) || desiredCrew.length === 0) {
    return flightStatusMessages[5]; // NO CREW REQUIREMENTS FOUND (likely no aircraft)
  }

  const assignedCrew: IFPSAssignedCrew[] = desiredCrew.map(
    (pos): IFPSAssignedCrew => ({
      positionCode: pos.positionCode,
      count: pos.count, // anything exceeding this will be considered extra crew.
      crew: [],
      isRequired: true,
    })
  );

  let requiredCrewCount = 0; // Total Crew count required for flight.
  desiredCrew.forEach((desired) => (requiredCrewCount = requiredCrewCount + desired.count));

  let totalCrewCount = 0; // Total count of all crew.
  let assignedCrewCount = 0; // Total count of filled crew slots.
  let extraSlots = 0;

  actualCrew.forEach((actual) => {
    if (actual.crew) {
      totalCrewCount = totalCrewCount + 1;
      const updateAssignedCrew = [...assignedCrew];
      const slotFound = updateAssignedCrew.findIndex(
        (slot) => slot.positionCode === actual.pos && slot.crew.length < slot.count && slot.isRequired
      );

      // If crew pos slot is available and doesn't exceed available crew count.
      if (slotFound > -1) {
        // ADD TO CREW SLOT
        assignedCrewCount = assignedCrewCount + 1;
        updateAssignedCrew[slotFound].crew.push(actual.crew);
      } else {
        // ADD TO EXTRA CREW (if "extra" slot exist for this pos type)
        const extraSlotFound = updateAssignedCrew.findIndex(
          (slot) => slot.positionCode === actual.pos && !slot.isRequired
        );

        if (extraSlotFound > -1) {
          // ADD TO EXTRA SLOT and increment it's count
          extraSlots++;
          const currentValue = updateAssignedCrew[extraSlotFound];
          const updateExtra: IFPSAssignedCrew = {
            ...currentValue,
            count: currentValue.count + 1,
            crew: [...currentValue.crew, actual.crew],
          };
          updateAssignedCrew[extraSlotFound] = updateExtra;
        } else {
          // Create an extra slot for this pos type, assign crew and add count.
          updateAssignedCrew.push({
            positionCode: actual.pos,
            count: 1,
            crew: [actual.crew],
            isRequired: false,
          });
        }
      }
    }
  });

  // If there are more crew in the pairings than available required slots to fill, show as extra.
  const extraCrew = totalCrewCount > assignedCrewCount;
  // If extra slots had to be created for this flight (not part of required crew).
  const extraSlotsCreated = extraSlots > 0;

  // console.log("extraCrew", extraCrew);
  // console.log("assignedCrew", assignedCrew);
  // console.log("total", totalCrewCount);
  // console.log("assigned", assignedCrewCount);

  switch (true) {
    case assignedCrewCount === 0:
      return flightStatusMessages[2]; // NO ACTUAL CREW (tested)
    case assignedCrewCount < requiredCrewCount:
      return flightStatusMessages[2]; // MISSING CREW (tested)
    case extraCrew:
      return flightStatusMessages[3]; // EXTRA CREW (tested)
    case extraSlotsCreated:
      return flightStatusMessages[4]; // EXTRA POSITION/SLOT (tested)
    case assignedCrewCount === requiredCrewCount && !extraCrew && !extraSlotsCreated:
      return flightStatusMessages[7]; // OK
    default:
      return flightStatusMessages[7]; // (catch-all remaining issues) - investigate creation of catch all error state.
  }
};

// Will look through assigned pairings to make sure they all meet the requirements defined by the flight
const returnFlightStatus = (fps: IFlightPairingStatus): IFPSStatus => {
  const { desiredCrew, actualCrew } = fps;
  return checkCrewSlots(desiredCrew, actualCrew);
};

/**
 *
 * @param fps - flight pairing status object from backend.
 * @returns - flight pairing status array the frontend can render.
 */
export const returnFlightPairingStatus = (fps: IFlightPairingStatus): IFPSFlightStatus => {
  switch (true) {
    case fps.pairingShortCodes?.length === 0: // NO PAIRINGS
      return { status: flightStatusMessages[1], pairings: null };
    default: {
      // PAIRING RELATED ERRORS
      const pairingStatusMap = returnPairingMap(fps?.actualCrew ?? []);
      const flightStatus = returnFlightStatus(fps);
      return { status: flightStatus, pairings: pairingStatusMap };
    }
  }
};
